import { useEffect, useState } from 'react';
import Button from './foundational/Button';
import { verificarMiPago, cancelarRenovacion } from '../lib/plan';
import { PRODUCTOS_PLUS, PRECIOS_ANUNCIADOS_CLP, precioClp } from '../lib/productosPlus';
import { isNative } from '../lib/platform';

const fecha = (iso) => new Intl.DateTimeFormat('es-CL', { timeZone: 'America/Santiago', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) + ' (hora de Chile)';

// Una misma explicación y cancelación en Inicio, Planes y Mi perfil. Nunca
// se afirma que habrá un cobro hasta leer la suscripción de esta cuenta.
export default function EstadoPruebaTarjeta({ onPagoConfirmado }) {
  const [estado, setEstado] = useState(null);
  const [error, setError] = useState('');
  const [paso, setPaso] = useState('idle');
  const cargar = async () => {
    setError('');
    try {
      const s = await verificarMiPago();
      if (!s) throw new Error('No pudimos comprobar tu suscripción.');
      setEstado(s);
    } catch (e) { setError(e.message || 'No pudimos comprobar tu suscripción.'); }
  };
  useEffect(() => {
    if (isNative) return undefined;
    let vivo = true;
    const refrescar = () => verificarMiPago().then(s => {
      if (!vivo) return;
      if (s) { setEstado(s); setError(''); }
      else setError('No pudimos comprobar tu suscripción.');
    }).catch(() => { if (vivo) setError('No pudimos comprobar tu suscripción.'); });
    refrescar();
    window.addEventListener('focus', refrescar);
    return () => { vivo = false; window.removeEventListener('focus', refrescar); };
  }, []);
  // Mantiene visible el resultado al vencer, aunque la persona deje abierta
  // la pantalla. El servidor reconcilia el pago; el reloj no concede acceso.
  useEffect(() => {
    if (isNative || !estado?.prueba || estado.status === 'active'
      || (estado.prueba.cancelada && Date.parse(estado.prueba.fin) <= Date.now())) return undefined;
    let vivo = true;
    const refrescar = () => verificarMiPago().then(s => {
      if (vivo && s) { setEstado(s); setError(''); }
    }).catch(() => { if (vivo) setError('No pudimos comprobar tu suscripción.'); });
    const restante = Date.parse(estado.prueba.fin) - Date.now();
    const timer = setTimeout(refrescar, restante > 0 ? Math.max(1000, Math.min(restante, 60000)) : 60000);
    return () => { vivo = false; clearTimeout(timer); };
  }, [estado]);
  useEffect(() => {
    if (estado?.status === 'active') onPagoConfirmado?.();
  }, [estado?.status, onPagoConfirmado]);
  if (isNative) return null;
  const prueba = estado?.prueba;
  const nombre = PRODUCTOS_PLUS[estado?.producto]?.nombre || 'tu membresía';
  const precio = PRECIOS_ANUNCIADOS_CLP[estado?.producto];
  const cancelar = async () => {
    setPaso('cancelando'); setError('');
    try { await cancelarRenovacion(); await cargar(); setPaso('idle'); }
    catch (e) { setPaso('idle'); setError(e.message || 'No pudimos cancelar. Intenta de nuevo.'); }
  };
  return (
    <section className="rounded-tk-md border border-edge bg-surface px-4 py-3 font-sans" aria-label="Estado de tu prueba y suscripción">
      <h2 className="text-body font-semibold text-ink">{estado?.status === 'active' ? `${nombre} está activa` : estado?.status === 'trial' ? 'Tu prueba está activa' : prueba ? 'Tu prueba terminó' : 'Tu suscripción'}</h2>
      {!estado && !error && <p className="mt-1 text-small text-ink-subtle dark:text-ink-muted">Comprobando la suscripción…</p>}
      {prueba && estado.status !== 'active' && (
        <p className="mt-2 text-small text-ink-subtle dark:text-ink-muted">
          {prueba.cancelada
            ? `La renovación está cancelada. No habrá un nuevo cobro. Tu prueba conserva el acceso hasta el ${fecha(prueba.fin)}.`
            : estado.status === 'trial'
              ? `El primer cobro de ${nombre}${precio ? ` será de ${precioClp(precio)} CLP` : ''} al terminar la prueba el ${fecha(prueba.fin)}. Después se renovará cada mes. Puedes cancelar antes desde aquí.`
              : estado.morosa
                ? 'No se pudo completar el cobro. Flow reintentará el cargo en tu tarjeta. El acceso pagado se activa cuando el pago se confirma.'
                : 'Estamos verificando el primer cobro en tu tarjeta. El acceso pagado se activa cuando el pago se confirma.'}
        </p>
      )}
      {prueba?.cancelada && Date.parse(prueba.fin) <= Date.now() && <a href="/?planes=1" className="inline-flex min-h-[44px] items-center text-small text-accent font-semibold">Contratar membresía</a>}
      {estado && !prueba && estado.status !== 'active' && <p className="mt-2 text-small text-ink-subtle dark:text-ink-muted">{estado.status === 'pending' ? 'Estamos verificando tu suscripción. Si falta completar el registro, vuelve a Planes.' : 'No hay una suscripción activa. Puedes revisar las opciones en Planes.'} <a href="/?planes=1" className="text-accent underline">Ver planes</a></p>}
      {estado?.status === 'active' && <p className="mt-2 text-small text-ink-subtle dark:text-ink-muted">El pago fue confirmado. Puedes revisar tu membresía en Mi perfil.</p>}
      {prueba && !prueba.cancelada && estado.status !== 'active' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {paso === 'confirmar' ? <>
            <p className="w-full text-small text-ink-subtle dark:text-ink-muted">¿Cancelar la renovación? Mantienes tu prueba hasta el {fecha(prueba.fin)}.</p>
            <Button size="sm" variant="secondary" onClick={cancelar}>Sí, cancelar</Button>
            <Button size="sm" variant="ghost" onClick={() => setPaso('idle')}>Volver</Button>
          </> : <Button size="sm" variant="secondary" onClick={() => setPaso('confirmar')} disabled={paso === 'cancelando'}>{paso === 'cancelando' ? 'Cancelando…' : 'Cancelar la renovación'}</Button>}
        </div>
      )}
      {error && <div className="mt-2"><p role="alert" className="text-small text-system-error">{error}</p><Button size="sm" variant="secondary" onClick={cargar}>Reintentar</Button></div>}
    </section>
  );
}
